import { IFetchResponse, IHIHTTP } from 'src/types/hip_types';
import tt from'../../../pip/library/pi-tt/pi-tt.js';

declare global {
  interface Window {
    ['HIP']: any;
  }
}

export interface IHTTPBeforeInterceptor {
  fn: <T>(uri: string, payload: T) => T;
  uriMatcher: string[];
}

export interface IHTTPAfterInterceptor {
  fn: <T>(uri: string, response: IFetchResponse, payload: T, httpEl: HTMLElement) => T;
  statusCode: number[];
}

export class HTTPInterceptor {
  readonly beforeHTTP: IHTTPBeforeInterceptor[] = [];
  readonly afterHTTP: IHTTPAfterInterceptor[] = [];
  readonly errorHTTP: IHTTPAfterInterceptor[] = [];

  private static __instance: HTTPInterceptor;

  static get instance() {
    return (HTTPInterceptor.__instance = HTTPInterceptor.__instance || new HTTPInterceptor());
  }

  constructor() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    window.HIP.aop.pointCut('hi-http', 'invoke', function (httpInvokeFn: Function) {
      return function (this: IHIHTTP, httpPayload: unknown) {
        const uri = this.uri;

        httpPayload = that.__applyBeforeInterceptors(uri, httpPayload, that);

        return httpInvokeFn
          .call(this, httpPayload)
          .then((httpObject: unknown) => {
            const response = tt.merge({}, this.response);

            return response.status >= 400
              ? that.__applyErrorInterceptors(uri, response, httpObject, this, that)
              : that.__applyAfterInterceptors(uri, response, httpObject, this, that);
          })
          .catch((httpObject: unknown) => {
            const response = tt.merge({}, this.response);

            that.__applyErrorInterceptors(uri, response, httpObject, this, that);
            return Promise.reject(httpObject);
          });
      };
    });
  }

  /**
   *
   * @param uri
   * @param httpPayload
   * @param configuration
   */
  private __applyBeforeInterceptors(uri: string, httpPayload: unknown, httpInterceptor: HTTPInterceptor) {
    const uriInterceptors = tt.filter((beforeInterceptor) => {
      return (
        beforeInterceptor.uriMatcher.length === 0 ||
        beforeInterceptor.uriMatcher.findIndex((s) => uri.indexOf(s) >= 0) >= 0
      );
    }, httpInterceptor.beforeHTTP);

    return tt.reduce(
      (_httpPayload, beforeInterceptor: IHTTPBeforeInterceptor) => {
        return beforeInterceptor.fn(uri, _httpPayload);
      },
      httpPayload,
      uriInterceptors
    );
  }

  /**
   *
   * @param uri
   * @param response
   * @param httpObject
   * @param configuration
   */
  private __applyAfterInterceptors(
    uri: string,
    response: IFetchResponse,
    httpObject: unknown,
    httpEl: HTMLElement,
    httpInterceptor: HTTPInterceptor
  ) {
    const statusInterceptors = tt.filter((afterInterceptor) => {
      return (
        afterInterceptor.statusCode.length === 0 ||
        afterInterceptor.statusCode.findIndex((statusCode) => response.status === statusCode) >= 0
      );
    }, httpInterceptor.afterHTTP);

    return tt.reduce(
      (_httpPayload, afterInterceptor: IHTTPAfterInterceptor) => {
        return afterInterceptor.fn(uri, response, _httpPayload, httpEl);
      },
      httpObject,
      statusInterceptors
    );
  }

  /**
   *
   * @param uri
   * @param response
   * @param httpObject
   * @param configuration
   */
  private __applyErrorInterceptors(
    uri: string,
    response: IFetchResponse,
    httpObject: unknown,
    httpEl: HTMLElement,
    httpInterceptor: HTTPInterceptor
  ) {
    const statusInterceptors = tt.filter((afterInterceptor) => {
      return (
        afterInterceptor.statusCode.length === 0 ||
        afterInterceptor.statusCode.findIndex((statusCode) => response.status === statusCode) >= 0
      );
    }, httpInterceptor.errorHTTP);

    return tt.reduce(
      (_httpPayload, errorInterceptor: IHTTPAfterInterceptor) => {
        return errorInterceptor.fn(uri, response, _httpPayload, httpEl);
      },
      httpObject,
      statusInterceptors
    );
  }
}
